import { SpringValue } from '@react-spring/core'
import { useFrame, useThree } from '@react-three/fiber'
import type { GroupProps } from '@react-three/fiber'
import type { Ref } from 'react'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import type { Group, ShaderMaterial, ShaderMaterialParameters } from 'three'
import { MeshPhysicalMaterial } from 'three'

import type { SpringPromise } from '../../@types/spring-promise'
import { containsVideoSource } from '../../lib/utils/containsVideoSource'
import { preloadGLTF, useGLTFLoader } from '../../lib/utils/useGLTFLoader'
import { modelLocation, Transitions } from './constants'
import { MuteButton } from './MuteButton'
import {
    createScreenTexture,
    getDynamicMaterialData,
    restartVideo,
} from './utils'

export interface GroupWithControls {
    obj: Group
    nextScreen: () => void
    prevScreen: () => void
}

export const Phone = forwardRef<GroupWithControls, PhoneProps>(
    (
        {
            screens,
            transition = Transitions.FADE,
            paused = false,
            onLoad,
            ...props
        },
        ref
    ) => {
        const {
            invalidate,
            performance: { regress },
        } = useThree()
        const phoneRef = useRef<Group | null>(null)
        const activeScreenIndex = useRef<number>(0)
        const screenMaterialRef = useRef<ShaderMaterialParameters | null>(null)
        const offsetSpring = useMemo(() => new SpringValue(0), [])
        const offsetSpringPromise = useRef<SpringPromise | null>(null)
        const loadingSpring = useMemo(
            () =>
                new SpringValue(0, {
                    config: { tension: 100 },
                    onChange: () => invalidate,
                }),
            [invalidate]
        )

        const bodyMaterial = useMemo(() => {
            return new MeshPhysicalMaterial({
                color: 'black',
                reflectivity: 0.25,
                clearcoat: 0.1,
                roughness: 0.5,
                sheen: 0.5,
            })
        }, [])

        const [muted, setMuted] = useState(true)
        const screensHaveVideo = containsVideoSource(screens)
        const mutedRef = useRef(true)
        const { nodes } = useGLTFLoader(modelLocation)
        const numToLoad = useRef(screens.length)

        const handleScreenLoad = useCallback(() => {
            numToLoad.current = numToLoad.current - 1
            if (screenMaterialRef.current && numToLoad.current <= 0) {
                loadingSpring.start(1)
            }
        }, [])

        const screenTextures = useMemo(
            () =>
                screens.map((screen) =>
                    createScreenTexture(screen, handleScreenLoad)
                ),
            [handleScreenLoad, screens]
        )

        const screenMaterialData = useMemo(
            () => getDynamicMaterialData(transition),
            [transition]
        )

        const nextScreen = useCallback(() => {
            activeScreenIndex.current += 1
            if (screenMaterialRef.current?.uniforms) {
                const { tCurrent, tNext } = screenMaterialRef.current.uniforms
                const previousTexture =
                    screenTextures[activeScreenIndex.current - 1]
                const currentTexture = screenTextures[activeScreenIndex.current]
                const nextTexture =
                    screenTextures[activeScreenIndex.current + 1]
                const hasNextScreen =
                    activeScreenIndex.current < screens.length - 1

                // mute previous texure if necessary
                if (previousTexture?.image?.tagName === 'VIDEO') {
                    previousTexture.image.pause()
                    previousTexture.image.muted = true
                }
                // restart previous texure and unmute if necessary
                if (currentTexture?.image?.tagName === 'VIDEO') {
                    restartVideo(currentTexture.image)
                    currentTexture.image.muted = mutedRef.current
                }

                const forward = () =>
                    offsetSpring.start({ from: 0, to: 1 }).then(() => {
                        tCurrent.value = currentTexture
                        offsetSpring.set(0)
                        if (hasNextScreen) tNext.value = nextTexture
                    })

                offsetSpringPromise.current =
                    offsetSpringPromise.current && offsetSpring.isAnimating
                        ? offsetSpringPromise.current.then(forward)
                        : forward()
            }
        }, [screenTextures])

        const prevScreen = useCallback(() => {
            activeScreenIndex.current -= 1
            if (screenMaterialRef.current?.uniforms) {
                const { tCurrent, tNext } = screenMaterialRef.current.uniforms
                const previousTexture =
                    screenTextures[activeScreenIndex.current + 1]
                const currentTexture = screenTextures[activeScreenIndex.current]
                const nextTexture =
                    screenTextures[activeScreenIndex.current + 1]
                const hasNextScreen =
                    activeScreenIndex.current < screens.length - 1

                // mute previous texure if necessary
                if (previousTexture?.image?.tagName === 'VIDEO') {
                    previousTexture.image.pause()
                    previousTexture.image.muted = true
                }
                // restart previous texure and unmute if necessary
                if (currentTexture?.image?.tagName === 'VIDEO') {
                    restartVideo(currentTexture.image)
                    currentTexture.image.muted = mutedRef.current
                }

                const reverse = () => {
                    tCurrent.value = currentTexture
                    offsetSpring.set(1)
                    if (hasNextScreen) tNext.value = nextTexture
                    return offsetSpring.start({ from: 1, to: 0 })
                }

                offsetSpringPromise.current =
                    offsetSpringPromise.current && offsetSpring.isAnimating
                        ? offsetSpringPromise.current.then(reverse)
                        : reverse()
            }
        }, [screenTextures])

        const initScreen = useCallback(() => {
            if (screenMaterialRef.current?.uniforms) {
                const { tCurrent, tNext } = screenMaterialRef.current.uniforms
                const currentTexture = screenTextures[activeScreenIndex.current]
                const nextTexture =
                    screenTextures[activeScreenIndex.current + 1]
                const hasNextScreen =
                    activeScreenIndex.current < screens.length - 1

                if (currentTexture?.image?.tagName === 'VIDEO') {
                    restartVideo(currentTexture.image)
                }

                tCurrent.value = currentTexture
                if (hasNextScreen) tNext.value = nextTexture
                offsetSpring.set(0)
            }
        }, [screenTextures])

        useFrame(() => {
            if (screenMaterialRef.current?.uniforms) {
                const { uBrightness, uOffset } =
                    screenMaterialRef.current.uniforms
                const loadValue = loadingSpring.get()
                uBrightness.value = loadValue
                uOffset.value = offsetSpring.get()
                // if (
                //     phoneRef.current &&
                //     (!loadingSpring.idle || loadValue === 0)
                // ) {
                //     phoneRef.current.position.y = (loadValue - 1) / 2
                // }
            }
        })

        const setRef = useCallback(
            (obj: Group) => {
                regress()
                if (ref && 'current' in ref) {
                    ref.current = {
                        obj,
                        nextScreen,
                        prevScreen,
                    }
                }
                if (onLoad) onLoad()
            },
            [regress, screenTextures, prevScreen]
        )

        useEffect(() => {
            if (ref && 'current' in ref && ref.current) {
                ref.current.nextScreen = nextScreen
                ref.current.prevScreen = prevScreen
            }
        }, [nextScreen, prevScreen])

        useEffect(() => {
            const currentTexture = screenTextures[activeScreenIndex.current]
            if (currentTexture?.image?.tagName === 'VIDEO') {
                if (paused) {
                    currentTexture.image.pause()
                } else {
                    restartVideo(currentTexture.image)
                }
            }
        }, [screenTextures, paused])

        useEffect(() => {
            initScreen()
            return () => {
                screenTextures.forEach((texture) => texture.dispose())
                loadingSpring.set(0)
            }
        }, [screenTextures])

        const toggleMute = useCallback(() => {
            setMuted((oldMuted) => {
                mutedRef.current = !oldMuted
                const currentTexture = screenTextures[activeScreenIndex.current]
                if (currentTexture?.image?.tagName === 'VIDEO') {
                    currentTexture.image.muted = mutedRef.current
                }
                return mutedRef.current
            })
        }, [screenTextures])

        return (
            <group ref={phoneRef}>
                <group {...props} ref={setRef}>
                    {screensHaveVideo && (
                        <MuteButton
                            muted={muted}
                            position={[0.8, 0.3, 0]}
                            onClick={toggleMute}
                        />
                    )}
                    <group
                        scale={0.32}
                        position={[0, 2.4, 0]}
                        rotation={[0, 0, 0]}
                    >
                        <group
                            position={[0.7, 0.56, 0.16]}
                            rotation={[-Math.PI, 0, -Math.PI]}
                        >
                            <mesh
                                geometry={nodes.volum_up_1.geometry}
                                material={nodes.volum_up_1.material}
                                position={[-2.78, 1.52, -0.01]}
                                rotation={[-Math.PI, 0, -Math.PI]}
                            />
                            <mesh
                                geometry={nodes.volum_up_2.geometry}
                                material={nodes.volum_up_2.material}
                                position={[-2.71, -2.61, -0.01]}
                                rotation={[-Math.PI, 0, -Math.PI]}
                            />
                            <mesh
                                geometry={nodes.volum_on_of.geometry}
                                material={nodes.volum_on_of.material}
                                position={[4.27, 3.55, 0.04]}
                            />
                            <mesh
                                geometry={nodes.volum_up_.geometry}
                                material={nodes.volum_up_.material}
                                position={[4.27, 2.21, -0.01]}
                            />
                            <mesh
                                geometry={nodes.volum__down.geometry}
                                material={nodes.volum__down.material}
                                position={[4.27, 0.8, -0.01]}
                            />
                        </group>
                        <group
                            position={[1.08, 4.93, -0.48]}
                            rotation={[-Math.PI, 0, -Math.PI]}
                        >
                            <mesh
                                geometry={nodes.camera5.geometry}
                                material={bodyMaterial}
                                position={[1.07, -0.85, -0.05]}
                            />
                            <mesh
                                geometry={nodes.camera4.geometry}
                                material={bodyMaterial}
                                position={[0.49, -1.15, -0.06]}
                            />
                            <mesh
                                geometry={nodes.camera3.geometry}
                                material={bodyMaterial}
                                position={[0.52, 1.46, -0.06]}
                            />
                            <mesh
                                geometry={nodes.camera2.geometry}
                                material={bodyMaterial}
                                position={[0.52, 0.14, -0.01]}
                            />
                            <mesh
                                geometry={nodes.camera1.geometry}
                                material={bodyMaterial}
                                position={[-1.15, 1.03, -0.01]}
                            />
                            <mesh
                                geometry={nodes.camera.geometry}
                                material={bodyMaterial}
                                position={[-1.15, -0.76, -0.01]}
                            />
                            <mesh
                                geometry={nodes.camera_frame.geometry}
                                material={bodyMaterial}
                                position={[-0.31, 0.14, -0.16]}
                            />
                        </group>
                        <group position={[-0.27, 7.06, 0.51]}>
                            <mesh
                                geometry={nodes.Notch_1.geometry}
                                material={bodyMaterial}
                                position={[0.27, -0.04, 0.03]}
                            />
                            <mesh
                                geometry={nodes.Speaker.geometry}
                                material={nodes.Speaker.material}
                                position={[0.38, 0.07, -0.05]}
                                rotation={[Math.PI / 2, 0, Math.PI / 2]}
                            />
                            <mesh
                                geometry={nodes.Lens.geometry}
                                material={nodes.Lens.material}
                                position={[-0.65, -0.04, 0.03]}
                            />
                        </group>
                        <mesh
                            geometry={nodes.Back.geometry}
                            material={bodyMaterial}
                            position={[-0.01, -0.04, 0.14]}
                            rotation={[-Math.PI, 0, -Math.PI]}
                        />
                        <mesh
                            geometry={nodes.Side.geometry}
                            material={bodyMaterial}
                            position={[-0.01, 0, 0.15]}
                            rotation={[-Math.PI, 0, -Math.PI]}
                        />
                        <mesh
                            geometry={nodes.Screen.geometry}
                            position={[-0.01, 0, 0.51]}
                            rotation={[-Math.PI, 0, -Math.PI]}
                            onClick={toggleMute}
                        >
                            <shaderMaterial
                                attach='material'
                                ref={screenMaterialRef as Ref<ShaderMaterial>}
                                {...screenMaterialData}
                            />
                        </mesh>
                    </group>
                </group>
            </group>
        )
    }
)

preloadGLTF(modelLocation)

interface PhoneProps extends GroupProps {
    screens: string[]
    transition?: Transitions
    paused?: boolean
    onLoad?: () => void
}
